import type { DestinationMiddlewareParams } from "@segment/analytics-next/dist/types/plugins/middleware";
import type { QueryClient } from "@tanstack/vue-query";
import { CategoryType, type ProductDetails } from "mws-ts-v2-sdk";

import { queries } from "~/queries";

const CATEGORY_VIEWED_EVENT_NAME = "Category Viewed";
const EVENT_VIEWED_EVENT_NAME = "Event Viewed";
const PRODUCT_VIEWED_EVENT_NAME = "Product Viewed";
const BID_PLACED_EVENT_NAME = "Bid Placed";
const REGISTRATION_SUCCEEDED_EVENT_NAME = "Registration Succeeded";

const mapSpecificEvents = (
  payload: DestinationMiddlewareParams["payload"],
  mapTeamEventToViewContent: (teamId: string) => any,
) => {
  switch (payload.obj.event) {
    case CATEGORY_VIEWED_EVENT_NAME: {
      const categoryType = payload.obj.properties?.category_type;
      if (
        categoryType &&
        categoryType === CategoryType.Team &&
        payload.obj.properties?.category_id
      ) {
        return mapTeamEventToViewContent(payload.obj.properties?.category_id);
      }

      break;
    }
    case EVENT_VIEWED_EVENT_NAME: {
      if (payload.obj.properties?.categories?.team) {
        return mapTeamEventToViewContent(
          payload.obj.properties?.categories?.team.id,
        );
      }

      break;
    }
    case BID_PLACED_EVENT_NAME: {
      // Facebook uses the eventID param for de-duplication instead of event_id.
      // For some reason, we need to send this as messageId
      if (payload.obj.properties?.bid_id) {
        payload.obj.messageId = payload.obj.properties?.bid_id;
      }
      break;
    }
    case REGISTRATION_SUCCEEDED_EVENT_NAME: {
      if (payload.obj.userId) {
        payload.obj.messageId = payload.obj.userId;
      }
    }
  }

  return null;
};

/**
 * We want to use the team id for the content_id in the facebook pixel events.
 * This is because the products are short-lived and trying to feed product ids
 * for Facebook ad optimisation doesn't result in good ad performance.
 */
export const facebookDestinationMiddleware =
  (queryClient: QueryClient, platformId: string) =>
  async ({ payload, next }: DestinationMiddlewareParams) => {
    if (payload.obj.type !== "track" || !payload.obj.properties) {
      return next(payload);
    }

    const setContentAsTeamId = (teamId: string) => {
      if (!payload.obj.properties) return;

      // for custom events, we need to manually map to content_id
      const contents = payload.obj.properties.contents;
      if (Array.isArray(contents)) {
        payload.obj.properties.contents = contents.map((content) => ({
          ...content,
          id: teamId,
        }));
      }

      payload.obj.properties.content_ids = [teamId];

      // To handle segments automatic mapping
      payload.obj.properties.product_id = teamId;
    };

    const mapTeamEventToViewContent = (teamId: string) => {
      payload.obj.event = PRODUCT_VIEWED_EVENT_NAME;

      setContentAsTeamId(teamId);

      return next(payload);
    };

    const eventResult = mapSpecificEvents(payload, mapTeamEventToViewContent);
    if (eventResult !== null) {
      return eventResult;
    }

    const productId = payload.obj.properties?.product_id;

    if (!productId) {
      return next(payload);
    }

    const productQueries = queryClient.getQueriesData<ProductDetails>({
      queryKey: queries.products.details._def,
    });

    const matchingProduct = productQueries
      .flatMap(([, data]) => data ?? [])
      .find((product) => product.id === productId);

    const setContentAsTeamIdFromProduct = (product: ProductDetails) => {
      if (!payload.obj.properties) return;

      const matchingTeam = product.categories.find(
        ({ externalReference }) =>
          externalReference?.type === CategoryType.Team,
      );

      if (!matchingTeam) return;

      setContentAsTeamId(matchingTeam.id);
    };

    if (matchingProduct) {
      setContentAsTeamIdFromProduct(matchingProduct);
    } else {
      const fetchedProduct = await queryClient.fetchQuery(
        queries.products.details(productId, platformId),
      );

      if (fetchedProduct) {
        setContentAsTeamIdFromProduct(fetchedProduct);
      }
    }

    return next(payload);
  };
